<template>
  <div class="echarts-chart-container">
    <v-chart class="chart" :option="chartOption" autoresize />
  </div>
</template>

<script>
// Importa il componente v-chart
import VChart from 'vue-echarts';

// Registra i componenti di ECharts che useremo
// È importante importare solo i moduli necessari per ridurre la dimensione del bundle
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { LineChart, ScatterChart } from 'echarts/charts'; 
import {
  GridComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent
} from 'echarts/components';

// Inietta i moduli in ECharts
use([
  CanvasRenderer,
  LineChart,
  ScatterChart, 
  GridComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent
]);

export default {
  name: 'EchartsLineChart',
  components: {
    VChart
  },
  props: {
    // chartData sarà un array di oggetti (es. [{date: '...', value: X}])
    // Questa prop riceverà i dati elaborati dall'esterno
    chartData: {
      type: Array,
      required: true
    },
    titleText: {
      type: String,
      default: 'Andamento Grandezza'
    },
    // NUOVA PROP: Un array di configurazioni per ogni serie (linea)
    // Es.: [{ name: 'Vento', dataKey: 'value' }, { name: 'Raffica', dataKey: 'gust' }]
    seriesConfig: {
      type: Array,
      required: true 
    },
      yAxisInterval: {
      type: Number,
      default: null // Lascia null per il calcolo automatico di default
    },
      yAxisMin: {
      type: Number,
      default: null // null lascia ECharts libero di determinare il minimo
    },
    yAxisMax: {
      type: Number,
      default: null // null lascia ECharts libero di determinare il massimo
    },
      chartType: {
      type: String,
      default: 'line' // Il default è 'line' (che può essere area)
    },
    // NUOVA PROP per abilitare/disabilitare il riempimento sotto la linea
    showArea: {
      type: Boolean,
      default: false // Il default è SENZA riempimento
    }



  },
  data() {
    return {
      // chartOption verrà calcolato tramite una computed property
      // per reagire ai cambiamenti di chartData
    };
  },
  computed: {
    chartOption() {
      // 1. Estrae le etichette per l'asse X (il tempo)
      const labels = this.chartData.map(item => item.date);

      // 2. Costruisce l'array di SERIE per ECharts
      const seriesArray = this.seriesConfig.map(config => {
        // Estrae i valori per questa specifica serie usando la sua 'dataKey'
        const values = this.chartData.map(item => item[config.dataKey]);
        // Verifica se mostrare l'area: solo se il tipo è 'line' E la prop showArea è true
        const shouldShowArea = this.chartType === 'line' && this.showArea === true;

        return {
          name: config.name,
          type: this.chartType,
          data: values, // I dati estratti
          
          // Applica areaStyle e stack SOLO se 'shouldShowArea' è true
          stack: shouldShowArea ? 'Total' : null, 
          areaStyle: shouldShowArea ? {} : null,
          // Echarts supporta marker diversi a seconda del tipo
          // Ad esempio, per 'scatter' potresti voler definire la dimensione dei punti
          symbolSize: this.chartType === 'scatter' ? 8 : null, 
           
          emphasis: {
            focus: 'series'
          },
        };
      });

      // 3. Estrae i nomi delle serie per la legenda
      const legendNames = this.seriesConfig.map(config => config.name);

      return {
        title: {
          text: this.titleText,
          left: 'center'
        },
        tooltip: {
          trigger: 'axis'
        },
        legend: {
          data: legendNames, // Lista di tutti i nomi delle serie
          top: 'bottom',
          bottom: '50px' // Esempio: Sposta la legenda 10px più in basso
        },
        grid: {
          left: '0%',
          right: '4%',
// Aumenta il 'bottom' per fare spazio alle etichette ruotate!
          bottom: '10%', // Aumenta questo valore (da 10% a 25% come prova)
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: labels,
// *** NUOVE OPZIONI PER LE ETICHETTE ***
          axisLabel: {
            // Opzione 1: Ruota di 90 gradi (Verticale)
            rotate: 45, 
            
            // Opzione 2 (Consigliata): Allineamento al basso
            // Se le ruoti di 90 gradi, l'etichetta dev'essere allineata in fondo.
            align: 'left', 
// AGGIUNGI QUESTO: Sposta l'etichetta al di sotto della linea dell'asse X
            verticalAlign: 'top', 
            
            // Spesso è necessario aggiungere un po' di spazio per evitare il taglio
            padding: [20, 0, 0, -50], // [top, right, bottom, left]            
            // Opzione 3 (Opzionale): Intervallo
            // Utile se ci sono troppe etichette e si sovrappongono.
             //interval: 50, // Mostra tutte le etichette (usare con cautela)
            // interval: 5 // Mostra 1 etichetta ogni 5
            
            // Opzione 4 (Opzionale): Formattazione
            // Puoi usare una funzione per estrarre solo HH:MM
            formatter: function (value) {
            //   // Assumendo che 'value' sia la stringa "AAAA/MM/GG HH:MM:SS"
            const day = value.substring(8, 10);
            const output = `{bold|${day}} (${value.substring(11, 16)}) `;
               return output; // Estrae HH:MM
             }, 
                   rich: {
              bold: {
                fontWeight: 'bolder' // Stile per il testo grassetto
              }
            },
       
            }
      },

      yAxis: {
          type: 'value',
          
          // Utilizza le props per min e max
          min: this.yAxisMin, 
          max: this.yAxisMax,

          // Mantiene l'intervallo dinamico se fornito
          interval: this.yAxisInterval 
        },


        series: seriesArray // Passa l'array dinamico di tutte le serie
      };
    }
  }, // Aggiungi stili per il contenitore del grafico
  // È importante dare una dimensione esplicita al contenitore
  // affinché ECharts possa renderizzare correttamente.
  // Puoi anche usare classi CSS.
  // Se la dimensione cambia, `autoresize` si occuperà di ridisegnare il grafico.
  style: `
    .echarts-chart-container {
      width: 100%;
      height: 400px; /* Imposta un'altezza fissa o basata su viewport */
    }
    .chart {
      width: 100%;
      height: 100%;
    }
  `
};
</script>

<style scoped>
.echarts-chart-container {
  width: 100%;
  height: 400px; /* O qualsiasi dimensione tu voglia */
  margin-top: 20px;
}
.chart {
  width: 100%;
  height: 100%;
}
</style>